import Actionlist, { Action } from "@asphalt-react/actionlist";
import Avatar from "@asphalt-react/avatar";
import { IconButton } from "@asphalt-react/button";
import { CaretDown, Edit, Power } from "@asphalt-react/iconpack";
import Logo, { Coffer } from "@asphalt-react/logo";
import Popover from "@asphalt-react/popover";
import Sidebar, { Nav, NavGroup, NavItem } from "@asphalt-react/sidebar";
import React, { useContext } from "react";
import { Link, useLocation } from "react-router-dom";
import { UserContext } from "../hooks/UserContext";
import routes from "../models/route-constants";
import styles from "./Sidebar.module.css";
import Permission from "../../authorization/components/Permission";
import actions from "../../authorization/models/actions";

const Footer = ({ user, signOut }) => {
  return (
    <>
      <div className={styles.Footer}>
        <Avatar size="s">
          <img
            src={user.photoUrl}
            alt="user avatar"
            aria-label="user avatar"
            style={{
              marginBottom: "0",
            }}
          />
        </Avatar>
        <div style={{ marginLeft: "0.5rem", lineHeight: 1.4 }}>
          <div style={{ fontSize: "1rem", fontWeight: "600" }}>{user.name}</div>
          <div style={{ fontSize: "0.875rem" }}>{user.role}</div>
        </div>
        <span style={{ marginLeft: "auto" }}>
          <Popover
            target={<IconButton nude icon={<CaretDown />} />}
            placement="top-end"
          >
            <Actionlist>
              {process.env.NODE_ENV === "development" &&
                user?.idToken && (
                  <Action
                    actionable
                    tagProps={{
                      onClick: async () => {
                        try {
                          await navigator.clipboard.writeText(user.idToken);
                          alert(
                            "ID token copied. Use as COFFER_ID_TOKEN for scripts (e.g. sync-darkroom)."
                          );
                        } catch (e) {
                          alert("Copy failed: " + (e?.message || String(e)));
                        }
                      },
                    }}
                    qualifier={<Edit />}
                  >
                    Copy ID token
                  </Action>
                )}
              <Action
                actionable
                tagProps={{
                  onClick: () => {
                    signOut();
                  },
                }}
                qualifier={<Power />}
              >
                Logout
              </Action>
            </Actionlist>
          </Popover>
        </span>
      </div>
    </>
  );
};

const SideBar = ({ signOut }) => {
  const { user } = useContext(UserContext);
  let currentPath = useLocation().pathname;
  const handleSelectItem = (event) => {};
  return (
    <div className={styles.sidebarContainer}>
      <Sidebar
        logo={
          <Link to="/" className="linkWithoutFocusStyles">
            <Logo product="Coffer" symbol={<Coffer />} />
          </Link>
        }
        footer={<Footer user={user} signOut={signOut} />}
        onSelect={(event) => handleSelectItem(event)}
      >
        <Nav>
          <NavGroup heading="Illustrations">
            <NavItem
              tagProps={{
                to: `/${routes.ILLUSTRATIONS}/${routes.ASSETS}`,
                replace: true,
              }}
              as={Link}
              active={currentPath.includes(
                `/${routes.ILLUSTRATIONS}/${routes.ASSETS}`
              )}
            >
              Assets
            </NavItem>
            <NavItem
              tagProps={{
                to: `/${routes.ILLUSTRATIONS}/${routes.REGIONS}`,
              }}
              as={Link}
              active={currentPath.includes(
                `${routes.ILLUSTRATIONS}/${routes.REGIONS}`
              )}
            >
              Regions
            </NavItem>
            <NavItem
              tagProps={{
                to: `/${routes.ILLUSTRATIONS}/${routes.APP_IDS}`,
              }}
              as={Link}
              active={currentPath.includes(
                `${routes.ILLUSTRATIONS}/${routes.APP_IDS}`
              )}
            >
              App IDs
            </NavItem>
            <NavItem
              tagProps={{
                to: `/${routes.ILLUSTRATION_BUILDER}`,
              }}
              as={Link}
              active={currentPath.includes(`${routes.ILLUSTRATION_BUILDER}`)}
            >
              Illustration Builder
            </NavItem>
          </NavGroup>
          <NavGroup heading="Icons">
            <NavItem
              tagProps={{
                to: `/${routes.ICONS}/${routes.ASSETS}`,
                replace: true,
              }}
              as={Link}
              active={currentPath.includes(`/${routes.ICONS}/${routes.ASSETS}`)}
            >
              Assets
            </NavItem>
          </NavGroup>
          <NavGroup heading="Tile Logos">
            <NavItem
              tagProps={{
                to: `/${routes.TILE_LOGO}/${routes.ASSETS}`,
                replace: true,
              }}
              as={Link}
              active={currentPath.includes(
                `/${routes.TILE_LOGO}/${routes.ASSETS}`
              )}
            >
              Assets
            </NavItem>
          </NavGroup>
          <NavGroup heading="Animated Assets">
            <NavItem
              tagProps={{
                to: `/${routes.ANIMATED_ASSETS}/${routes.ILLUSTRATIONS}`,
                replace: true,
              }}
              as={Link}
              active={currentPath.includes(
                `/${routes.ANIMATED_ASSETS}/${routes.ILLUSTRATIONS}`
              )}
            >
              Animated Illustrations
            </NavItem>
            <NavItem
              tagProps={{
                to: `/${routes.ANIMATED_ASSETS}/${routes.ICONS}`,
                replace: true,
              }}
              as={Link}
              active={currentPath.includes(
                `/${routes.ANIMATED_ASSETS}/${routes.ICONS}`
              )}
            >
              Animated Icons
            </NavItem>
          </NavGroup>
          <Permission action={actions.ASSIGN_DEVELOPER_PERMISSION}>
            <NavGroup heading="Admin">
              <NavItem
                tagProps={{ to: `/${routes.ADMINS}` }}
                as={Link}
                active={currentPath.includes(`/${routes.ADMINS}`)}
              >
                User Permissions
              </NavItem>
            </NavGroup>
          </Permission>
          <NavGroup heading="Help">
            <NavItem
              tagProps={{
                to: `/${routes.HELP}/${routes.USAGE_GUIDE}`,
                replace: true,
              }}
              as={Link}
              active={currentPath.includes(
                `/${routes.HELP}/${routes.USAGE_GUIDE}`
              )}
            >
              How to use
            </NavItem>
            <NavItem
              tagProps={{
                to: `/${routes.HELP}/${routes.BCS_FLOW_GUIDE}`,
              }}
              as={Link}
              active={currentPath.includes(
                `/${routes.HELP}/${routes.BCS_FLOW_GUIDE}`
              )}
            >
              BCS Config guide
            </NavItem>
            <NavItem
              tagProps={{
                to: `/${routes.HELP}/${routes.CONTACT_US}`,
              }}
              as={Link}
              active={currentPath.includes(
                `${routes.HELP}/${routes.CONTACT_US}`
              )}
            >
              Contact us
            </NavItem>
          </NavGroup>
        </Nav>
      </Sidebar>
    </div>
  );
};
export default SideBar;
